import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, CheckCircle2, Zap, Tag, Bell, MapPin, Loader2, XCircle, Clock, Sparkles } from 'lucide-react';
import { API_URL, authApi } from '@/lib/api';

/**
 * Public coupon redemption page. Anyone can view.
 * Booth staff who own the coupon (exhibitor role + same company) see a "Mark Redeemed" button.
 */
export default function RedeemPage() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const [redeemedNow, setRedeemedNow] = useState(false);
  const [myCompanyId, setMyCompanyId] = useState(null);

  const fetchCoupon = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/coupons/${encodeURIComponent(code)}`);
      setData(res.data.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.message || 'Coupon not found');
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => { fetchCoupon(); }, [fetchCoupon]);

  // If a logged-in exhibitor visits this page, look up their linked company so we
  // can hide/show the "Mark Redeemed" button based on ownership.
  useEffect(() => {
    if (!authApi.isAuthenticated() || !authApi.isExhibitor()) return;
    axios
      .get(`${API_URL}/exhibitor/me`, { headers: { Authorization: `Bearer ${authApi.getToken()}`, Accept: 'application/json' } })
      .then((r) => setMyCompanyId(r.data?.data?.company?.id || null))
      .catch(() => setMyCompanyId(null));
  }, []);

  const remain = useCountdown(data?.expires_at);

  // Only show the staff button when this coupon belongs to the logged-in exhibitor's company
  const isOwner = !!(myCompanyId && data?.company?.id && myCompanyId === data.company.id);
  const canRedeem = isOwner;

  const redeem = async () => {
    setRedeeming(true);
    try {
      const res = await axios.post(`${API_URL}/exhibitor/coupons/${encodeURIComponent(code)}/redeem`, {}, {
        headers: { Authorization: `Bearer ${authApi.getToken()}`, Accept: 'application/json' },
      });
      setData((prev) => ({ ...prev, ...res.data.data }));
      setRedeemedNow(true);
    } catch (e) {
      alert(e.response?.data?.message || 'Redemption failed');
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><Loader2 className="w-8 h-8 text-emerald-400 animate-spin" /></div>;
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-950 via-slate-900 to-red-950 flex items-center justify-center p-6">
        <div className="max-w-md text-center bg-slate-900/70 rounded-2xl p-8 border border-red-500/30" data-testid="coupon-not-found">
          <XCircle className="w-12 h-12 mx-auto text-red-400 mb-4" />
          <h2 className="text-white font-bold text-xl mb-2">Coupon not found</h2>
          <p className="text-slate-400 text-sm mb-6">{error || 'The code you scanned is invalid.'}</p>
          <Link to="/" className="inline-flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300 text-sm font-bold"><ArrowLeft className="w-4 h-4" /> Back to map</Link>
        </div>
      </div>
    );
  }

  const badgeColor = data.type === 'flash_sale' ? 'from-red-500 to-orange-500' : data.type === 'offer' ? 'from-emerald-500 to-teal-500' : 'from-blue-500 to-indigo-500';
  const BadgeIcon = data.type === 'flash_sale' ? Zap : data.type === 'offer' ? Tag : Bell;

  const expiredLabel = data.is_expired
    ? 'This coupon has expired.'
    : !data.is_active
    ? 'This coupon is paused by the exhibitor.'
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-slate-900 to-emerald-950 p-4 sm:p-6" data-testid="redeem-page">
      <div className="max-w-md mx-auto">
        <Link to="/" className="inline-flex items-center gap-1.5 text-slate-400 hover:text-white text-xs mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to map
        </Link>

        {/* Ticket card */}
        <div className="bg-white rounded-3xl overflow-hidden shadow-2xl">
          {/* Top gradient section */}
          <div className={`bg-gradient-to-br ${badgeColor} p-6 text-white relative`}>
            <div className="flex items-start gap-3">
              <div className="bg-white/20 backdrop-blur rounded-xl p-3">
                <BadgeIcon className="w-7 h-7" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest font-bold opacity-90">
                  {data.type === 'flash_sale' ? 'FLASH DEAL' : data.type === 'offer' ? 'SPECIAL OFFER' : 'ANNOUNCEMENT'}
                </div>
                <h1 className="text-xl font-black leading-tight mt-1" data-testid="coupon-title">{data.title}</h1>
                {data.company && (
                  <div className="text-sm mt-1 opacity-95">by {data.company.name}</div>
                )}
              </div>
            </div>

            {data.discount_label && (
              <div className="mt-4 bg-yellow-300 text-slate-900 rounded-xl py-3 px-4 text-center font-black text-2xl tracking-wider shadow-lg" data-testid="coupon-discount">
                {data.discount_label}
              </div>
            )}

            {remain && !remain.expired && data.is_live && (
              <div className="mt-4 flex items-center justify-center gap-2 text-yellow-100 font-bold bg-black/25 backdrop-blur rounded-full py-2 px-3">
                <Clock className="w-4 h-4" /> Ends in {remain.mm}:{remain.ss}
              </div>
            )}
          </div>

          {/* Ticket divider notches */}
          <div className="relative">
            <div className="absolute -left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-slate-900" />
            <div className="absolute -right-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-slate-900" />
            <div className="border-t-2 border-dashed border-slate-200 mx-4"></div>
          </div>

          {/* Coupon code */}
          <div className="p-6">
            <div className="text-center">
              <div className="text-slate-500 text-[10px] uppercase tracking-widest font-bold">Show this code at the booth</div>
              <div className="mt-2 font-mono text-3xl font-black text-slate-900 tracking-[0.35em] bg-slate-100 rounded-lg py-3 px-4 select-all" data-testid="coupon-code">
                {data.coupon_code}
              </div>
            </div>

            {data.booth && (
              <div className="mt-4 flex items-center gap-2 justify-center text-slate-600 text-sm">
                <MapPin className="w-4 h-4" />
                Booth <Link to={`/booth/${data.booth.id}`} className="font-bold text-emerald-600 hover:underline">{data.booth.booth_number}</Link>
              </div>
            )}

            {data.body && (
              <div className="mt-4 text-slate-600 text-sm text-center whitespace-pre-wrap">{data.body}</div>
            )}

            {/* Status banners */}
            {expiredLabel && (
              <div className="mt-5 flex items-center gap-2 justify-center text-red-600 font-bold text-sm bg-red-50 border border-red-200 rounded-lg py-2 px-3" data-testid="coupon-expired-label">
                <XCircle className="w-4 h-4" /> {expiredLabel}
              </div>
            )}

            {data.redemption_count > 0 && (
              <div className="mt-4 text-center text-slate-500 text-xs">
                Redeemed {data.redemption_count} time{data.redemption_count === 1 ? '' : 's'}
                {data.last_redeemed_at && <> · last {new Date(data.last_redeemed_at).toLocaleTimeString()}</>}
              </div>
            )}

            {/* Staff action */}
            {canRedeem && data.is_live && (
              <button
                onClick={redeem}
                disabled={redeeming}
                className="w-full mt-5 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="mark-redeemed-btn"
              >
                {redeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : redeemedNow ? <CheckCircle2 className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
                {redeemedNow ? 'Redeemed — Scan next visitor' : 'Mark as Redeemed'}
              </button>
            )}

            {!canRedeem && data.is_live && (
              <div className="mt-5 text-center text-slate-500 text-[11px]" data-testid="visitor-instructions">
                {authApi.isExhibitor() && myCompanyId
                  ? <>This coupon belongs to a different booth. Only <span className="font-bold">{data.company?.name}</span> staff can mark it redeemed.</>
                  : <>Booth staff: <Link to="/login" className="text-emerald-600 font-bold underline">login</Link> as an exhibitor to mark this coupon redeemed.</>
                }
              </div>
            )}
          </div>
        </div>

        {/* Bottom link */}
        <div className="text-center mt-6 text-slate-500 text-[11px]">
          Hari Landskap Negara 2026 · Coupon system
        </div>
      </div>
    </div>
  );
}

function useCountdown(iso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!iso) return undefined;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [iso]);
  if (!iso) return null;
  const diff = new Date(iso).getTime() - now;
  if (diff <= 0) return { mm: '00', ss: '00', expired: true };
  const mm = String(Math.floor(diff / 60000)).padStart(2, '0');
  const ss = String(Math.floor((diff % 60000) / 1000)).padStart(2, '0');
  return { mm, ss, expired: false };
}
